/**
 * Ce qu'un écran a montré la dernière fois, pour le remontrer tout de suite à la prochaine
 * ouverture pendant que la version fraîche arrive (la base est loin : chaque lecture coûte
 * 200 à 600 ms en ligne). En mémoire pour la session, et sur l'appareil quand c'est léger
 * (listes, profil). Le compte fait partie de la clé ; la déconnexion efface tout.
 */
const PREFIX = "editit.keep.";
const MAX_STORED = 200_000; // caractères : au-delà, la donnée reste en mémoire seulement
// Les vignettes et liens des réponses sont signés pour 24 h : au-delà de 12 h, rien n'est remontré.
const MAX_AGE_MS = 12 * 3600_000;
const memory = new Map<string, unknown>();
let owner = "";
/**
 * Les clés gardées sur l'appareil (`compte|clé`), relevées une seule fois : oublier un projet après chaque retouche
 * parcourait tout le stockage du navigateur (brouillons, préférences…), à chaque écriture.
 */
let stored: Set<string> | null = null;
function storedKeys(): Set<string> {
  if (stored) return stored;
  stored = new Set();
  try {
    for (const k of Object.keys(localStorage)) if (k.startsWith(PREFIX)) stored.add(k.slice(PREFIX.length));
  } catch {
    /* stockage indisponible : rien n'y est gardé */
  }
  return stored;
}

/** Ceux qui gardent d'autres choses pour la session (listes en cache, projets suivis) : prévenus de ce qui est périmé. */
type Forgetter = (stale: (key: string) => boolean, accountChanged: boolean) => void;
const forgetters = new Set<Forgetter>();
export function onForget(fn: Forgetter): () => void {
  forgetters.add(fn);
  return () => void forgetters.delete(fn);
}
const tell = (stale: (key: string) => boolean, accountChanged: boolean) => forgetters.forEach((fn) => fn(stale, accountChanged));

/** Le compte dont on garde les écrans (fixé à la connexion). */
export function keepFor(uid: string | null) {
  if (uid === owner) return;
  owner = uid ?? "";
  memory.clear();
  tell(() => true, true);
}

export function kept<T>(key: string): T | undefined {
  if (!owner) return undefined;
  const k = `${owner}|${key}`;
  if (memory.has(k)) return memory.get(k) as T;
  try {
    const raw = localStorage.getItem(PREFIX + k);
    if (raw) {
      const { at, value } = JSON.parse(raw) as { at: number; value: T };
      if (Date.now() - at > MAX_AGE_MS) return undefined;
      memory.set(k, value);
      return value;
    }
  } catch {
    /* stockage indisponible (navigation privée) : rien de gardé */
  }
  return undefined;
}

export function keep(key: string, value: unknown) {
  if (!owner) return;
  const k = `${owner}|${key}`;
  memory.set(k, value);
  try {
    const raw = JSON.stringify({ at: Date.now(), value });
    if (raw.length <= MAX_STORED) {
      localStorage.setItem(PREFIX + k, raw);
      storedKeys().add(k);
    } else {
      localStorage.removeItem(PREFIX + k);
      storedKeys().delete(k);
    }
  } catch {
    /* plein ou indisponible : la mémoire suffit */
  }
}

/**
 * Ce qu'une écriture de l'app rend faux, d'après son adresse :
 * - une retouche d'un montage (`/v1/projects/{id}/…`) : ce montage seulement ;
 * - ce qui change sa carte dans les listes (le créer, le renommer, le supprimer, le dupliquer, le lancer, l'arrêter,
 *   l'exporter, sa publication et ses chiffres) : ce montage et les listes de l'espace (une carte périmée s'ouvrait) ;
 * - une autre écriture (bibliothèque, kits, Studio…) : les listes de l'espace ;
 * - l'espace lui-même (le créer, le renommer) : tout, profil compris ; la mémoire du monteur : rien.
 */
export type WriteScope = { project: string | null; lists: boolean; all: boolean };
const LIST_WRITES = /^\/(duplicate|start|cancel|approve|exports|publication)(\/|$)/;
export function writeScope(path: string): WriteScope {
  const clean = path.split("?")[0];
  if (/^\/v1\/workspaces(\/[^/]+)?\/?$/.test(clean)) return { project: null, lists: true, all: true };
  // La mémoire du monteur (`/v1/me/memory…`) : aucun écran gardé n'en dépend.
  if (/^\/v1\/me(\/|$)/.test(clean)) return { project: null, lists: false, all: false };
  const m = /^\/v1\/projects(?:\/([^/]+)(\/.*)?)?$/.exec(clean);
  if (!m) return { project: null, lists: true, all: false };
  if (!m[1]) return { project: null, lists: true, all: false }; // POST /v1/projects : une carte de plus
  let project = m[1];
  try {
    project = decodeURIComponent(m[1]);
  } catch {
    /* identifiant déjà lisible */
  }
  return { project, lists: !m[2] || LIST_WRITES.test(m[2]), all: false };
}

/** Une clé gardée (`projects:<espace>`, `studio:<espace>`, `me`…) est-elle périmée après cette écriture ? */
export function staleKey(scope: WriteScope, key: string): boolean {
  if (scope.all) return true;
  if (scope.project !== null && key.includes(scope.project)) return true;
  return scope.lists && key !== "me";
}

/**
 * Après une modification faite par l'app, les écrans gardés qu'elle touche ne sont plus vrais : ils se rechargent au
 * lieu de remontrer une donnée d'avant. Sans adresse : tout. Une retouche ne vide plus tout le reste (les listes de
 * l'espace restaient vraies et se relisaient pour rien).
 */
export function forgetScreens(path?: string) {
  const scope = path === undefined ? { project: null, lists: true, all: true } : writeScope(path);
  const stale = (key: string) => staleKey(scope, key);
  // Les clés en mémoire et sur l'appareil portent le compte devant (`compte|clé`).
  const mine = (k: string) => stale(k.slice(k.indexOf("|") + 1));
  for (const k of [...memory.keys()]) if (mine(k)) memory.delete(k);
  const keys = storedKeys();
  try {
    for (const k of [...keys])
      if (mine(k)) {
        localStorage.removeItem(PREFIX + k);
        keys.delete(k);
      }
  } catch {
    /* rien */
  }
  tell(stale, false);
}

/** À la déconnexion : rien d'un compte ne reste sur l'appareil. */
export function forgetAll() {
  memory.clear();
  owner = "";
  try {
    for (const k of Object.keys(localStorage)) if (k.startsWith(PREFIX)) localStorage.removeItem(k);
  } catch {
    /* rien */
  }
  stored = new Set();
  tell(() => true, true);
}
